import {
  DashedDropdown,
  DashedDropdownProps,
  DropdownOption,
} from '@praetorian-chariot/ui';
import { useMemo } from 'react';

import { Loader } from '@/components/Loader';
import { useStorage } from '@/utils/storage/useStorage.util';

type DropdownOptionWithTooltip = DropdownOption & {
  tooltip?: string;
};

type GroupOption = DropdownOptionWithTooltip & {
  isGroup?: boolean;
  items?: DropdownOptionWithTooltip[];
};

export function SearchableMultiSelectDropdown({
  isLoading,
  label,
  options,
  selectedValues,
  multiSelect = true,
  isSearchable = true,
  isPraetorianUser = false,
  onApply,
  onClear,
  searchValue: parentSearchValue,
  setSearchValue: parentSetSearchValue,
  isLoadingOptions,
  onDropdownStateChange,
  ...restProps
}: Omit<DashedDropdownProps, 'selected' | 'onApply'> & {
  isLoading: boolean;
  label: string;
  selectedValues: string[];
  isSearchable?: boolean;
  isPraetorianUser?: boolean;
  onApply?: (selected: DropdownOptionWithTooltip[]) => void;
  onClear?: () => void;
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  isLoadingOptions?: boolean;
  onDropdownStateChange?: (isOpen: boolean, id: string) => void;
}) {
  // Use external search state if provided, otherwise use internal state
  const [searchValue, setSearchValue] = useStorage(
    {
      parentState: parentSearchValue,
      onParentStateChange: parentSetSearchValue,
    },
    parentSearchValue ?? ''
  );

  // Helper function to find an option by value in the original options structure
  const findOptionByValue = (
    value: string,
    opts: GroupOption[]
  ): DropdownOptionWithTooltip | undefined => {
    for (const opt of opts) {
      if (opt.value === value) return opt;
      if (opt.isGroup && opt.items) {
        const found = findOptionByValue(value, opt.items);
        if (found) return found;
      }
    }
    return undefined;
  };

  // Create a stable reference for options to avoid circular reference issues
  const optionsRef = useMemo(() => options, [JSON.stringify(options)]);

  // Create enhanced options that include both current options and any previously selected options that might not be in the current set
  const enhancedOptions = useMemo(() => {
    const selectedOptionsNotInCurrent: DropdownOptionWithTooltip[] = [];

    // Find selected values that don't exist in current options
    for (const value of selectedValues) {
      const existsInCurrent = findOptionByValue(
        value,
        optionsRef as GroupOption[]
      );
      if (!existsInCurrent) {
        selectedOptionsNotInCurrent.push({
          id: value,
          label: value,
          value: value,
        });
      }
    }

    // Combine current options with any missing selected options
    return [...optionsRef, ...selectedOptionsNotInCurrent] as GroupOption[];
  }, [optionsRef, selectedValues]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return enhancedOptions;

    // Create a set of selected values for quick lookup
    const selectedValuesSet = new Set(selectedValues);

    return enhancedOptions
      .map(option => {
        if (option.isGroup && option.items) {
          // Filter items in the group, but keep selected items regardless of search
          const filteredItems = option.items.filter(
            item =>
              item.label.toLowerCase().includes(searchValue.toLowerCase()) ||
              (item.value && selectedValuesSet.has(item.value.toString()))
          );

          // Return group with filtered items
          return {
            ...option,
            items: filteredItems,
          };
        }
        // For non-group options, just check if they match the search
        return option;
      })
      .filter(option => {
        if (option.isGroup && option.items) {
          // Keep group if it has matching items or its own label matches
          return (
            option.items.length > 0 ||
            option.label.toLowerCase().includes(searchValue.toLowerCase())
          );
        }
        // Keep non-group option if its label matches, it's selected, or it's a previously selected option
        return (
          option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
          (option.value && selectedValuesSet.has(option.value.toString()))
        );
      });
  }, [enhancedOptions, searchValue, selectedValues]);

  const selected = useMemo(() => {
    const selectedOptions: DropdownOptionWithTooltip[] = [];

    // Deduplicate selectedValues to avoid duplicate options
    const uniqueSelectedValues = [...new Set(selectedValues)];

    // Process each selected value - now all selected values should be found in enhancedOptions
    for (const value of uniqueSelectedValues) {
      const option = findOptionByValue(value, enhancedOptions);
      if (option) {
        selectedOptions.push(option);
      }
    }

    return selectedOptions;
  }, [enhancedOptions, selectedValues]);

  return (
    <div className="relative">
      <Loader
        className="absolute top-0 left-0 z-10 size-full rounded-2xl"
        isLoading={isLoading}
      />
      <DashedDropdown
        size="xs"
        {...restProps}
        selected={multiSelect ? selected : selected[0]}
        label={label}
        options={filteredOptions}
        active={selected.length > 0}
        multiSelect={multiSelect}
        onApply={onApply}
        onClear={onClear}
        isPraetorianUser={isPraetorianUser}
        {...(isSearchable
          ? {
              searchValue,
              onSearchChange: setSearchValue,
              searchPlaceholder: `Search ${label}...`,
            }
          : {})}
        isLoadingOptions={isLoadingOptions}
        title={`Select ${label}`}
        virtualized={filteredOptions.length > 10}
        onDropdownStateChange={onDropdownStateChange}
      />
    </div>
  );
}
